// src/features/marketAlerts/useMarketAlerts.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api';

export type AlertType = 'price_above' | 'price_below' | 'local_vs_player_market' | 'volume_discrepancy';

export interface MarketAlert {
  id: number;
  item_id: number;
  item_name: string;
  alert_type: AlertType;
  threshold: string; // NUMERIC comes back as a string
  account_for_perks: boolean;
  channel_id: string;
  mention_role_id: string | null;
  cooldown_ms: number;
  expires_at: string | null;
  enabled: boolean;
  last_triggered_at: string | null;
  created_by: string;
  created_at: string;
}

export interface ItemMatch {
  id: number;
  name: string;
}

export function useMarketAlerts(guildId: string | null) {
  return useQuery({
    queryKey: ['market-alerts', guildId],
    queryFn: () => apiFetch<MarketAlert[]>(`/api/market-alerts/${guildId}`),
    enabled: !!guildId,
  });
}

export function useItemSearch(query: string) {
  return useQuery({
    queryKey: ['item-search', query],
    queryFn: () => apiFetch<ItemMatch[]>(`/api/items/search?q=${encodeURIComponent(query)}`),
    enabled: query.trim().length >= 2,
  });
}

export interface CreateAlertInput {
  itemId: number;
  alertType: AlertType;
  threshold: number;
  accountForPerks?: boolean;
  channelId: string;
  mentionRoleId?: string | null;
  cooldownMs?: number;
  expiresAt?: string | null;
}

export function useCreateAlert(guildId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAlertInput) =>
      apiFetch(`/api/market-alerts/${guildId}`, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['market-alerts', guildId] }),
  });
}

export function useUpdateAlert(guildId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: number } & Partial<CreateAlertInput> & { enabled?: boolean }) =>
      apiFetch(`/api/market-alerts/${guildId}/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['market-alerts', guildId] }),
  });
}

export function useDeleteAlert(guildId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiFetch(`/api/market-alerts/${guildId}/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['market-alerts', guildId] }),
  });
}
